// Bloc « Études » du tableau de bord : où en est-on, et quelles études mener
// ensuite.
//
// Le DIAGNOSTIC (constats et recommandations) est calculé par le serveur, par
// des règles nommées : chaque recommandation dit POURQUOI elle existe. L'IA
// n'intervient que sur demande, pour rédiger une synthèse lisible par un PO ou
// un dirigeant — elle ne choisit pas les études.
//
// Les cartes (créer, reformuler, écarter) sont celles de `Recommendations.tsx`,
// partagées avec la page Études.
import { useStudyPlan, useStudySynthesis } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { PlanFinding } from '../api/types';
import { Button, ErrorBanner, Eyebrow, Stat } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import { RecommendationList } from './Recommendations';
import './plan.css';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function StudyPlanBlock({ projectId }: { projectId: string }) {
  const plan = useStudyPlan(projectId);
  const synth = useStudySynthesis(projectId);
  const canWrite = useCanWrite(projectId);

  if (plan.isPending) return null;
  if (plan.isError) {
    return (
      <section className="zy-card plan-block" role="status">
        <Eyebrow>Études · orientation</Eyebrow>
        <p className="zy-texte-muet">
          {plan.error instanceof ApiRequestError && plan.error.status === 404
            ? 'Le serveur n’est pas à jour : redémarrez-le pour activer le plan d’études.'
            : 'Le plan d’études est momentanément indisponible.'}
        </p>
      </section>
    );
  }

  const { findings, recommendations } = plan.data;
  const open = recommendations.filter((r) => r.status === 'open');

  return (
    <section className="zy-card plan-block" aria-labelledby="plan-title">
      <div className="plan-head">
        <div>
          <Eyebrow>Études · orientation</Eyebrow>
          <h2 className="t-h3" id="plan-title">Où en est-on, et quoi étudier ensuite</h2>
          <p className="card-lede">
            Synthèse de l’état actuel, confrontant la mesure 0 du client aux preuves réunies, et
            études recommandées par ordre de priorité.
          </p>
        </div>
        {canWrite ? (
          <Button variant="ghost" loading={synth.isPending} onClick={() => synth.mutate()}>
            {synth.data ? 'Réécrire la synthèse' : 'Rédiger la synthèse avec l’IA'}
          </Button>
        ) : null}
      </div>

      {synth.error ? <ErrorBanner>{errorMessage(synth.error)}</ErrorBanner> : null}
      {synth.data ? (
        <div className="plan-synthesis" aria-live="polite">
          <p className="plan-synthesis-label">
            Synthèse rédigée par l’IA{synth.data.mode === 'fake' ? ' (mode démonstration)' : ''} — à partir des constats
            ci-dessous, sans rien y ajouter
          </p>
          <p>{synth.data.synthesis.summary}</p>
          {synth.data.synthesis.focus ? <p className="plan-focus">{synth.data.synthesis.focus}</p> : null}
        </div>
      ) : null}

      {findings.length > 0 ? (
        <ul className="plan-findings" aria-label="Constats">
          {findings.map((f) => (
            <Finding key={f.text} finding={f} />
          ))}
        </ul>
      ) : null}

      <div className="plan-recs-head">
        <h3 className="plan-recs-title">
          Études recommandées
          <span className="plan-count">{open.length}</span>
        </h3>
      </div>
      <RecommendationList projectId={projectId} recommendations={recommendations} canWrite={canWrite} limit={4} />
    </section>
  );
}

/** Les chiffres de la recherche — études, tests, verbatims, insights, piliers
 *  prouvés : ce qu'on regarde d'abord, en tête du tableau de bord. */
export function ResearchStats({ projectId }: { projectId: string }) {
  const plan = useStudyPlan(projectId);
  if (!plan.data) return null;
  const s = plan.data.snapshot;
  return (
    <div className="plan-stats research-stats" role="group" aria-label="La recherche en chiffres">
      <Stat label="Études" value={s.studies} />
      <Stat label="Tests" value={s.tests} />
      <Stat label="Verbatims" value={s.verbatims} />
      <Stat label="Insights" value={s.insights} />
      <Stat label="Piliers prouvés" value={<>{s.pillarsProven}<small>/{s.pillars}</small></>} />
    </div>
  );
}

function Finding({ finding }: { finding: PlanFinding }) {
  const label = { good: 'Point fort', warn: 'À surveiller', bad: 'Point d’alerte', neutral: 'Constat' }[finding.tone];
  return (
    <li className={`plan-finding plan-finding-${finding.tone}`}>
      <span className="plan-dot" aria-hidden="true" />
      <span className="sr-only">{label} : </span>
      {finding.text}
    </li>
  );
}
